import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  User, Phone, Calendar, DollarSign, CheckCircle2, 
  AlertCircle, CreditCard, FileText, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

export default function FiadoPublicoPage() {
  const { token } = useParams();
  const [fiado, setFiado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFiado = async () => {
      try {
        const res = await axios.get(`${API}/fiado/publico/${token}`);
        setFiado(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Link inválido ou expirado");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchFiado();
    } else {
      setError("Token não fornecido");
      setLoading(false);
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800/50 border-red-500/50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Erro</h2>
            <p className="text-slate-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isParcelaAberta = fiado.tipo_pagamento === "parcela_aberta";
  const isPago = fiado.situacao === "pago";
  
  // Valor da assinatura (apenas para exibição, não entra nos cálculos)
  const valorAssinatura = fiado.valor_assinatura || 0;
  const entrada = fiado.entrada || 0;
  
  // Função para obter valor da parcela (individual ou default) - SEM assinatura
  const getValorParcela = (parcela) => {
    return parcela.valor || fiado.valor_parcela;
  };
  
  // Cálculo para parcelas fixas (SEM assinatura)
  const parcelasPagas = fiado.parcelas?.filter(p => p.status === "paga") || [];
  const parcelasPendentes = fiado.parcelas?.filter(p => p.status === "pendente") || [];
  const totalParcelas = fiado.parcelas?.length || 0;
  const valorPagoFixas = parcelasPagas.reduce((sum, p) => sum + getValorParcela(p), 0);
  const valorPendenteFixas = parcelasPendentes.reduce((sum, p) => sum + getValorParcela(p), 0);
  
  // Cálculo para parcela aberta
  const pagamentos = fiado.pagamentos || [];
  const totalPagamentos = pagamentos.reduce((sum, p) => sum + p.valor, 0);
  const valorPagoAberta = totalPagamentos + entrada;
  const valorPendenteAberta = Math.max(0, fiado.valor_total - valorPagoAberta);
  
  // Valores finais baseados no tipo
  const valorPagoFinal = isParcelaAberta ? valorPagoAberta : (valorPagoFixas + entrada);
  const valorPendenteFinal = isParcelaAberta ? valorPendenteAberta : valorPendenteFixas;
  
  const progressPercent = !isParcelaAberta && totalParcelas > 0
    ? Math.round((parcelasPagas.length / totalParcelas) * 100)
    : fiado.valor_total > 0 
      ? Math.min(100, Math.round((valorPagoFinal / fiado.valor_total) * 100))
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Consulta de Pagamento
          </h1>
          <p className="text-slate-400">Visualize seus pagamentos e pendências</p>
        </div>

        {/* Cliente Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-3 text-white">
              <User className="w-6 h-6 text-emerald-500" />
              {fiado.nome}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {fiado.descricao && (
              <div className="p-3 bg-slate-700/50 rounded-lg border-l-4 border-emerald-500">
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 text-sm">{fiado.descricao}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-slate-400">
              <Calendar className="w-4 h-4" />
              <span>Início: {fiado.data_inicio}</span>
            </div>

            <div className="flex items-center gap-2">
              <Badge className={isPago ? "bg-emerald-500" : "bg-amber-500"}>
                {isPago ? "✅ PAGO" : "⏳ PENDENTE"}
              </Badge>
              <Badge variant="outline" className={isParcelaAberta ? "text-blue-400 border-blue-400" : "text-purple-400 border-purple-400"}>
                {isParcelaAberta ? "Parcela Aberta" : "Parcelas Fixas"}
              </Badge>
            </div>

            {/* Produto */}
            {fiado.produto && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <div className="flex items-center gap-2">
                  <span className="text-purple-400 text-sm font-medium">Produto:</span>
                  <span className="text-white font-bold">{fiado.produto}</span>
                </div>
              </div>
            )}

            {/* Detalhamento Parcela + Assinatura */}
            {/* Detalhamento da assinatura (apenas informativo) */}
            {valorAssinatura > 0 && (
              <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <div className="flex items-center gap-2 text-orange-400">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">
                    {isParcelaAberta ? (
                      <>Assinatura mensal: <strong className="text-orange-300">{formatCurrency(valorAssinatura)}</strong></>
                    ) : (
                      <>Parcela: {formatCurrency(fiado.valor_parcela)} + Assinatura: {formatCurrency(valorAssinatura)} = <strong className="text-orange-300">{formatCurrency(fiado.valor_parcela + valorAssinatura)}</strong></>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Entrada paga */}
            {entrada > 0 && (
              <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">Entrada paga: <strong>{formatCurrency(entrada)}</strong></span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Resumo Financeiro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-emerald-500/10 border-emerald-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-bold text-emerald-500">PAGO</span>
              </div>
              <p className="text-2xl font-bold text-emerald-500">
                {formatCurrency(valorPagoFinal)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-bold text-amber-500">PENDENTE</span>
              </div>
              <p className="text-2xl font-bold text-amber-500">
                {formatCurrency(valorPendenteFinal)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-bold text-blue-500">VALOR A PAGAR</span>
              </div>
              <p className="text-2xl font-bold text-blue-500">
                {formatCurrency(fiado.valor_total)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Barra de Progresso */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-400">Progresso do pagamento</span>
              <span className={`text-sm font-bold ${progressPercent === 100 ? 'text-emerald-400' : 'text-white'}`}>
                {progressPercent}%
              </span>
            </div>
            <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${
                  progressPercent === 100 
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                    : 'bg-gradient-to-r from-blue-500 to-blue-400'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {!isParcelaAberta && totalParcelas > 0 && (
              <p className="text-xs text-slate-500 mt-2 text-center">
                {parcelasPagas.length} de {totalParcelas} parcelas pagas
              </p>
            )}
          </CardContent>
        </Card>

        {/* Detalhes de Pagamentos */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              {isParcelaAberta ? "Histórico de Pagamentos" : "Parcelas"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isParcelaAberta ? (
              // Lista de pagamentos para parcela aberta
              <div className="space-y-3">
                {fiado.pagamentos && fiado.pagamentos.length > 0 ? (
                  fiado.pagamentos.map((pag, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30"
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <div>
                          <p className="font-bold text-emerald-400">{formatCurrency(pag.valor)}</p>
                          <p className="text-xs text-slate-400">{pag.data}</p>
                        </div>
                      </div>
                      {pag.descricao && (
                        <span className="text-xs text-slate-400">{pag.descricao}</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhum pagamento registrado ainda</p>
                  </div>
                )}
              </div>
            ) : (
              // Grid de parcelas para parcelas fixas
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {fiado.parcelas && fiado.parcelas.map((parcela, index) => {
                  const isPaga = parcela.status === "paga";
                  const valorParcela = getValorParcela(parcela);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        isPaga 
                          ? "bg-emerald-500/10 border-emerald-500/50" 
                          : "bg-slate-700/50 border-slate-600"
                      }`}
                    >
                      <p className="text-xs font-bold text-slate-400 mb-1">
                        Parcela {parcela.numero}/{totalParcelas}
                      </p>
                      <p className="text-sm font-semibold text-white mb-2">
                        {formatCurrency(valorParcela)}
                      </p>
                      {isPaga ? (
                        <>
                          <div className="flex items-center justify-center gap-1 text-emerald-500">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs font-bold">PAGA</span>
                          </div>
                          {parcela.data_pagamento && (
                            <p className="text-xs text-slate-400 mt-1">{parcela.data_pagamento}</p>
                          )}
                        </>
                      ) : (
                        <div className="flex items-center justify-center gap-1 text-amber-500">
                          <Clock className="w-4 h-4" />
                          <span className="text-xs font-bold">PENDENTE</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm pt-4">
          <p>Esta é uma consulta de visualização apenas.</p>
          <p>Para dúvidas, entre em contato com o responsável.</p>
        </div>
      </div>
    </div>
  );
}
